import React from 'react';
import qs from 'query-string';
import { TFunction } from 'i18next';

/**
 * Returns an object created by key-value entries given by URL search parameter.
 */
export const useSearchParams = () => {
    /**
     * @note - Currently not supported until Node 14, currently the App runs Node 12
     * const searchParams = new URLSearchParams(document.location.search);
     * const params = Object.fromEntries(searchParams);
     */
    const params = qs.parse(document.location.search);
    return params;
};

export const isValidKeyboardEventKey = <T>(event: React.KeyboardEvent<T>): boolean => {
    /** On key press, prevent default behaviour */
    event.preventDefault();
    /** Check if key is 'Space' or 'Enter'  */
    switch (event.key) {
        case ' ':
            return true;
        case 'Enter':
            return true;
        default:
            return false;
    }
};

export type OnClickEvent<T = Element> = React.MouseEvent<T>;

export const handleOnClickEvent = (fn: <T>(event: OnClickEvent<T>) => void) => <T>(event: OnClickEvent<T>): void => {
    event.preventDefault();
    fn(event);
};

export type KeyEvent<T = Element> = React.KeyboardEvent<T>;

export const handleKeyEvent = (fn: <T>(event: KeyEvent<T>) => void) => <T>(event: KeyEvent<T>): void => {
    event.preventDefault();
    if (isValidKeyboardEventKey(event)) {
        fn(event);
    }
};

export function getReadableBytes(bytes: number) {
    if (bytes === 0) {
        return '0 B';
    }
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    return `${(bytes / 1024 ** i).toFixed(2)} ${sizes[i]}`;
}

// calls an array of functions one after another with the same arguments
export const callAll = (...fns: (Function | undefined)[]) => (...args: any[]) => fns.forEach((fn) => fn && fn(...args));

export const getTotalHeaderHeight = (): number => {
    const headerElement = document.getElementById('header');
    if (headerElement) {
        return headerElement.offsetHeight;
    }
    return 88;
};

export const getTotalFooterHeight = () => {
    const MCConnectFooter = document.getElementsByTagName('connect-footer');
    return (MCConnectFooter[0] as HTMLElement)?.offsetHeight ?? 0;
};

export function getPathnameAtLevel(pathname: string, level: number) {
    // if you have a pathname /fruit/22/likes/cat and level is 2
    // /fruits/22: fruit is level 1, 22 is level 2
    return pathname
        .split('/')
        .filter((val) => val !== '')
        .reduce((acc, curr) => {
            let newAcc = acc;
            if (newAcc.split('/').filter((val) => val !== '').length < level) {
                newAcc = `${acc}/${curr}`;
            }
            return newAcc;
        }, '');
}

/**
 * Parses a query string into an key value pair object
 */
export const parseQueryString = (queryString: string) => {
    if (queryString === '') return {};
    return qs.parse(queryString);
};

/**
 * @param thisString string with merchant, merchants, MID label to replace
 * @param t react-i18next t function
 * @returns string with replaced merchant labels
 */
export const replaceAllMerchantLabels = (thisString: string, t: TFunction) => {
    const newString = (thisString || '')
        .replace('Merchants', t('Merchants'))
        .replace('Merchant', t('Merchant'))
        .replace('MID', t('MID'))
        .replace('Identifier', `${t('Merchant')} (Identifier)`);

    return newString;
};

export const resolveRuleType = (rule: Rule) => {
    let ruleType;
    if (rule.isTenantOverride || rule.inherit === 'TENANT_GROUP') {
        ruleType = 'Override';
    } else if (rule.expression) {
        ruleType = 'Advanced';
    } else {
        ruleType = 'Simple';
    }
    return ruleType;
};

export const getDeployedRegionFromUrl = (url: string): string | undefined => {
    // url format: https://ui.uat.uae.rules.brighterion.io
    const urlTokens = url.split('.');
    const deployedRegionIndex = urlTokens.indexOf('rules') - 1;

    if (deployedRegionIndex <= 0) {
        return undefined;
    }

    return urlTokens[deployedRegionIndex];
};
